<script lang="ts">
  import { onMount, onDestroy } from "svelte"
  import { browser } from "$app/environment"
  import { DendriticNetwork } from "./dendritic/DendriticNetwork.js"
  import { SimpleRenderer } from "./dendritic/SimpleRenderer"
  import { DendriticAnimation } from "./dendritic/DendriticAnimation"
  import type { NetworkParams } from "./dendritic/NetworkParams"
  import { debugLoggingEnabled } from "../stores/debugStore"
  import { debugLog } from "../utils/debug"

  let container: HTMLElement | null = null
  let animationId: number
  let clock: any
  let mouse: any
  let raycaster: any

  // Modules
  let THREE: any
  let EffectComposer: any
  let RenderPass: any
  let UnrealBloomPass: any

  // Core system components
  let network: DendriticNetwork
  let renderer: SimpleRenderer
  let animation: DendriticAnimation

  // --- RECEIVE ALL UI PARAMETERS AS PROPS ---
  export let uiBloomStrength: number
  export let uiBloomRadius: number
  export let uiBloomThreshold: number
  export let uiActivationSpeed: number
  export let uiPropagationChance: number
  export let uiDecayRate: number
  export let uiPulseIntensity: number
  export let uiSpontaneousRate: number
  export let uiBranchGrowthRate: number
  export let uiBranchPruningRate: number
  export let uiStrengthDecayRate: number
  export let uiEnableParticles: boolean
  export let uiEnableDendrites: boolean
  export let enableLogging: boolean
  export let uiFogEnabled: boolean
  export let uiFogColor: string
  export let uiFogDensity: number

  // Update the debug store when the prop changes
  $: $debugLoggingEnabled = enableLogging

  // Pulse Burst Particle System
  let burstParticles: any[] = []
  let burstSprite: any = null
  function createBurstSprite(THREE: any) {
    const size = 64
    const canvas = document.createElement("canvas")
    canvas.width = canvas.height = size
    const ctx = canvas.getContext("2d")!
    ctx.clearRect(0, 0, size, size)
    let gradient = ctx.createRadialGradient(
      size / 2,
      size / 2,
      0,
      size / 2,
      size / 2,
      size / 2,
    )
    gradient.addColorStop(0, "rgba(56,189,248,0.9)")
    gradient.addColorStop(0.2, "rgba(56,189,248,0.4)")
    gradient.addColorStop(0.6, "rgba(56,189,248,0.05)")
    gradient.addColorStop(1, "rgba(56,189,248,0)")
    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI)
    ctx.fill()
    for (let i = 0; i < 3; i++) {
      const offset = (i + 1) * 5
      const angle = (i * Math.PI * 2) / 3
      const offsetX = Math.cos(angle) * offset
      const offsetY = Math.sin(angle) * offset
      gradient = ctx.createRadialGradient(
        size / 2 + offsetX,
        size / 2 + offsetY,
        0,
        size / 2 + offsetX,
        size / 2 + offsetY,
        size / 4,
      )
      gradient.addColorStop(0, "rgba(255,255,255,0.6)")
      gradient.addColorStop(1, "rgba(255,255,255,0)")
      ctx.fillStyle = gradient
      ctx.beginPath()
      ctx.arc(size / 2 + offsetX, size / 2 + offsetY, size / 4, 0, 2 * Math.PI)
      ctx.fill()
    }
    const texture = new THREE.Texture(canvas)
    texture.needsUpdate = true
    return texture
  }

  function spawnBurst(THREE: any, scene: any, pos: any, count = 24) {
    if (!burstSprite) burstSprite = createBurstSprite(THREE)
    const basePos = pos.clone ? pos.clone() : { x: pos.x, y: pos.y, z: pos.z }
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      const speed = 0.7 + Math.random() * 0.7
      const dir = {
        x: Math.sin(phi) * Math.cos(angle) * speed,
        y: Math.sin(phi) * Math.sin(angle) * speed,
        z: Math.cos(phi) * speed,
      }
      const particle = {
        mesh: null,
        pos: basePos.clone ? basePos.clone() : { ...basePos },
        dir,
        age: 0,
        life: 0.7 + Math.random() * 0.5,
        size: 10 + Math.random() * 6,
        opacity: 0.45 + Math.random() * 0.25,
      }
      const geometry = new THREE.BufferGeometry()
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(
          [particle.pos.x, particle.pos.y, particle.pos.z],
          3,
        ),
      )
      const material = new THREE.PointsMaterial({
        map: burstSprite,
        size: particle.size,
        transparent: true,
        opacity: particle.opacity,
        depthTest: false,
        sizeAttenuation: true,
        blending: THREE.AdditiveBlending,
        color: 0x38bdf8,
      })
      const points = new THREE.Points(geometry, material)
      scene.add(points)
      particle.mesh = points
      burstParticles.push(particle)
    }
  }

  // The 'params' object is now built inside onMount to ensure props are available
  let params: NetworkParams

  async function init() {
    debugLog("DendriticBackground init started.")
    if (!container || !browser) {
      return
    }

    try {
      debugLog("Importing THREE.js modules...")
      const [threeModule, postprocessingModule] = await Promise.all([
        import("three"),
        import("three/examples/jsm/postprocessing/EffectComposer.js"),
      ])
      THREE = threeModule
      EffectComposer = postprocessingModule.EffectComposer

      const { RenderPass: RP } = await import(
        "three/examples/jsm/postprocessing/RenderPass.js"
      )
      const { UnrealBloomPass: UBP } = await import(
        "three/examples/jsm/postprocessing/UnrealBloomPass.js"
      )
      RenderPass = RP
      UnrealBloomPass = UBP
      debugLog("Post-processing modules imported.")

      clock = new THREE.Clock()
      raycaster = new THREE.Raycaster()
      mouse = new THREE.Vector2()
      debugLog("Core components initialized.")

      network = new DendriticNetwork(THREE, params)
      debugLog("DendriticNetwork instantiated.")
      network.createNeurons()
      network.createDendriticBranches()
      network.pruneUnattachedNeurons()

      renderer = new SimpleRenderer(
        THREE,
        EffectComposer,
        RenderPass,
        UnrealBloomPass,
        network,
        params,
        clock,
      )
      debugLog("SimpleRenderer instantiated.")
      await renderer.initializeRenderer(container)
      renderer.createNeuronBodies()
      renderer.createDendrites()
      renderer.createActivationPulses()

      animation = new DendriticAnimation(
        THREE,
        network,
        renderer,
        params,
        clock,
      )
      debugLog("DendriticAnimation instantiated.")

      const origTriggerActivation = animation.triggerActivation.bind(animation)
      animation.triggerActivation = (
        neuronId: string,
        intensity: number = 1.0,
      ) => {
        origTriggerActivation(neuronId, intensity)
        if (intensity > 0.15 && network && uiEnableParticles) {
          const neuron = network.getNeuronById(neuronId)
          if (neuron) {
            spawnBurst(THREE, renderer.scene, neuron.position, 10)
          }
        }
      }

      debugLog("Starting animation loop.")
      animate()
    } catch (error) {
      debugLog("Failed to initialize dendritic background:", error)
    }
  }

  function animate() {
    if (!browser || !renderer || !animation || !clock) return

    try {
      animationId = requestAnimationFrame(animate)
      const elapsedTime = clock.getElapsedTime()

      // Update parameters directly from the component's props
      params.bloomStrength = uiBloomStrength
      params.bloomRadius = uiBloomRadius
      params.bloomThreshold = uiBloomThreshold
      params.activationSpeed = uiActivationSpeed
      params.propagationChance = uiPropagationChance
      params.decayRate = uiDecayRate
      params.pulseIntensity = uiPulseIntensity
      params.spontaneousActivationRate = uiSpontaneousRate
      params.branchGrowthRate = uiBranchGrowthRate
      params.branchPruningRate = uiBranchPruningRate
      params.strengthDecayRate = uiStrengthDecayRate
      params.enableDendrites = uiEnableDendrites

      renderer.updateBloomPass(uiBloomStrength, uiBloomRadius, uiBloomThreshold)
      if (renderer && renderer.scene) {
        if (uiFogEnabled) {
          renderer.scene.fog = new THREE.FogExp2(uiFogColor, uiFogDensity)
        } else {
          renderer.scene.fog = null
        }
      }
      animation.generateSpontaneousActivations()
      animation.reinforceStrongPathways()
      animation.updateNeurons()
      animation.updatePulses()
      network.updateNetworkTopology(elapsedTime)
      renderer.updateVisuals(elapsedTime)

      // particle update logic
      const startColor = new THREE.Color(0x38bdf8)
      const endColor = new THREE.Color(0xffffff)
      for (let i = burstParticles.length - 1; i >= 0; i--) {
        const p = burstParticles[i]
        p.age += 0.016
        p.pos.x += p.dir.x * 0.18
        p.pos.y += p.dir.y * 0.18
        p.pos.z += p.dir.z * 0.18
        const positions = p.mesh.geometry.attributes.position.array
        positions[0] = p.pos.x
        positions[1] = p.pos.y
        positions[2] = p.pos.z
        p.mesh.geometry.attributes.position.needsUpdate = true
        const t = p.age / p.life
        p.mesh.material.opacity = Math.max(0, p.opacity * (1 - t))
        p.mesh.material.size = p.size * (1 + t * 2.2)
        p.mesh.material.color.copy(startColor.clone().lerp(endColor, t))
        if (p.age > p.life) {
          renderer.scene.remove(p.mesh)
          p.mesh.geometry.dispose()
          p.mesh.material.dispose()
          burstParticles.splice(i, 1)
        }
      }

      renderer.updateCamera(elapsedTime)
      renderer.render()
    } catch (error) {
      debugLog("Animation error:", error)
    }
  }

  function onWindowResize() {
    if (renderer) renderer.onWindowResize()
  }
  function onMouseClick(event: MouseEvent) {
    if (animation && mouse && raycaster && renderer?.camera) {
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1
      animation.onMouseClick(mouse, raycaster, renderer.camera)
    }
  }

  onMount(() => {
    // Define the params object here, using the props passed from the parent.
    params = {
      neuronCount: 80,
      networkSize: { x: 250, y: 120, z: 250 },
      neuronBodySize: 1.2,
      neuronGlowSize: 6.0,
      neuronColor: 0x3366cc,
      glowColor: 0x66aaff,
      maxDendriticBranches: 15,
      maxBranchOrder: 8,
      branchingProbability: 0.95,
      branchLengthRange: { min: 20, max: 40 },
      branchAngleVariation: Math.PI / 1.5,
      dendriticFieldRadius: 70,
      connectionRadius: 80,
      maxConnectionsPerNeuron: 6,
      synapticStrengthRange: { min: 0.8, max: 1.0 },
      activationThreshold: 0.05,
      refractoryPeriod: 120,
      conductionSpeed: 40,
      synapticDelay: 25,
      dendriteColor: 0x4488cc,
      dendriteRadius: 0.4,
      activationSpeed: uiActivationSpeed,
      propagationChance: uiPropagationChance,
      decayRate: uiDecayRate,
      pulseIntensity: uiPulseIntensity,
      spontaneousActivationRate: uiSpontaneousRate,
      bloomStrength: uiBloomStrength,
      bloomRadius: uiBloomRadius,
      bloomThreshold: uiBloomThreshold,
      branchGrowthRate: uiBranchGrowthRate,
      branchPruningThreshold: 0.1,
      branchPruningRate: uiBranchPruningRate,
      maxNetworkBranches: 250,
      growthStrengthThreshold: 0.5,
      learningRate: 0.01,
      strengthDecayRate: uiStrengthDecayRate,
      pruningThreshold: 0.05,
      growthThreshold: 0.5,
      neuronInactivityThreshold: 1000,
      neuronHealthDecayRate: 0.001,
      minNeuronHealthForSurvival: 0.1,
      enableDendrites: uiEnableDendrites,
    }
    init()
  })

  onDestroy(() => {
    if (animationId) cancelAnimationFrame(animationId)
    if (renderer && container) {
      try {
        container.removeChild(renderer.renderer.domElement)
        renderer.dispose()
      } catch (e) {
        console.warn("Error during cleanup:", e)
      }
    }
    for (const p of burstParticles) {
      if (p.mesh) {
        renderer.scene.remove(p.mesh)
        p.mesh.geometry.dispose()
        p.mesh.material.dispose()
      }
    }
    burstParticles = []
  })
</script>

<svelte:window on:resize={onWindowResize} on:click={onMouseClick} />

<div bind:this={container} class="neural-background"></div>

<style>
  .neural-background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    overflow: hidden;
  }
  .neural-background :global(canvas) {
    display: block;
  }
</style>
